import { FRAMING, anglesOf, clearLine, grown, inside, panelOutline, roundBounds, spanOf, toRig, } from './lectern-view.js';
import { NAVIGATION_ASSEMBLY } from './lectern.js';
/**
 * Where the ⓘ of a world feature's points sits (ADR-087): on the side of the feature's round the
 * pupil sees, in the framed view where the feature is in it, never behind the lectern. It reuses the
 * framing's own geometry -- the feature's angular outline (`spanOf`), the lectern's outline grown by
 * the margin a swung lectern keeps (`panelOutline`, `FRAMING.swingMargin`) and the clear line -- so
 * the badge and the lectern agree on what the lectern hides. Pure, for the headset and its tests.
 *
 * Angles are degrees in the rig, azimuth positive to the right, elevation up, as in framing.ts.
 */
export const FEATURE_BADGE = {
    /** Up from the round's centre, as a share of its angular half-height. */
    lift: 1 / 3,
    /** Off a lectern that covers it, in steps of this many degrees. */
    step: 1,
    /** Never further from the round's centre than this share of its angular half-height or half-width. */
    reach: 0.9,
    /** Inside the round: at most this many metres ahead. */
    insideDistance: 4,
    /** Inside the round: this many degrees above the clear line. */
    insideAbove: 8,
};
const sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const length = (v) => Math.hypot(v[0], v[1], v[2]);
const RAD = Math.PI / 180;
/** The lectern's outline as the eyes see it, grown by the margin a swung lectern keeps. */
function lecternOutline(eyeHeight, placement, assembly) {
    return grown(panelOutline(eyeHeight, placement.side, placement.degrees, {
        distance: placement.distance,
        assembly,
    }), FRAMING.swingMargin);
}
/** Whether the lectern, standing at `placement`, hides the point `at` (in the room) from the eyes. */
export function lecternCovers(rig, at) {
    const outline = lecternOutline(rig.eyeHeight ?? rig.eye[1], rig.placement, rig.assembly);
    return inside(anglesOf(toRig(at, rig.eye, rig.yaw)), outline);
}
/** A unit direction in the room for rig angles: the inverse of `anglesOf(toRig(…))`. */
function fromRig(az, el, yaw) {
    const x = Math.cos(el * RAD) * Math.sin(az * RAD);
    const y = Math.sin(el * RAD);
    const z = -Math.cos(el * RAD) * Math.cos(az * RAD);
    const c = Math.cos(yaw);
    const s = Math.sin(yaw);
    return [x * c + z * s, y, -x * s + z * c];
}
/**
 * Where the ray from `eye` along the unit `direction` meets the round's ellipsoid (`radius` across,
 * `height` up), as distances along it; undefined when it misses.
 */
function crossings(round, eye, direction) {
    const scale = [1 / round.radius, 1 / round.height, 1 / round.radius];
    const o = sub(eye, round.centre).map((v, i) => v * scale[i]);
    const d = direction.map((v, i) => v * scale[i]);
    const a = d[0] ** 2 + d[1] ** 2 + d[2] ** 2;
    const b = o[0] * d[0] + o[1] * d[1] + o[2] * d[2];
    const c = o[0] ** 2 + o[1] ** 2 + o[2] ** 2 - 1;
    const disc = b * b - a * c;
    if (disc < 0)
        return undefined;
    const root = Math.sqrt(disc);
    return { near: (-b - root) / a, far: (-b + root) / a, inside: c <= 0 };
}
/** The point of the round's ellipsoid on the line from its centre to the eyes. */
function towardEyes(round, eye) {
    const v = sub(eye, round.centre);
    const n = Math.hypot(v[0] / round.radius, v[1] / round.height, v[2] / round.radius);
    if (n === 0)
        return [round.centre[0], round.centre[1] + round.height, round.centre[2]];
    return [round.centre[0] + v[0] / n, round.centre[1] + v[1] / n, round.centre[2] + v[2] / n];
}
/**
 * Where a world feature's badge sits for a pupil at `eye`, the rig turned by `yaw`, the lectern at
 * `placement`:
 *
 * - Outside the round: on its near surface, where the ray from the eyes enters it; that ray a third
 *   of the round's angular half-height above its centre, and at the azimuth nearest its centre that
 *   is both on it and in the framed view (±`FRAMING.wideHalf`; its centre when none is). When the
 *   lectern hides that point it goes up a degree at a time (to 0.9 of its half-height above its
 *   centre, and never above `FRAMING.topElevation`), then sideways away from the lectern (to 0.9 of
 *   its half-width, and never out of the framed view); the first point the lectern leaves free, else the first one, `covered`.
 * - Inside the round (the pupil stands in what the world is about): straight ahead, 8° above the
 *   clear line, at most 4 m away and inside the round.
 * - When that ray misses the round (a flat one under the eyes): the point of it facing the eyes,
 *   `covered` judged there.
 */
export function featureBadgeAnchor(input) {
    const { round, eye, yaw, placement } = input;
    const eyeHeight = input.eyeHeight ?? eye[1];
    const assembly = input.assembly ?? NAVIGATION_ASSEMBLY;
    const outline = lecternOutline(eyeHeight, placement, assembly);
    const covers = (az, el) => inside([az, el], outline);
    // As spanOf decides it: within its larger half-size of the centre, the round is all around.
    const inRound = length(sub(eye, round.centre)) <= Math.max(round.radius, round.height);
    let az;
    let el;
    if (inRound) {
        az = 0;
        el = Math.min(clearLine(eyeHeight, assembly) + FEATURE_BADGE.insideAbove, FRAMING.topElevation);
    }
    else {
        const span = spanOf(roundBounds(round), eye, yaw);
        const az0 = (span.azMin + span.azMax) / 2;
        const halfAz = (span.azMax - span.azMin) / 2;
        const el0 = span.centreEl;
        const halfEl = (span.elMax - span.elMin) / 2;
        // In the framed view where it overlaps the feature; on the feature where it does not.
        const lo = Math.max(span.azMin, -FRAMING.wideHalf);
        const hi = Math.min(span.azMax, FRAMING.wideHalf);
        az = lo <= hi ? Math.min(Math.max(az0, lo), hi) : az0;
        el = Math.min(el0 + halfEl * FEATURE_BADGE.lift, FRAMING.topElevation);
        if (covers(az, el)) {
            const found = (() => {
                const top = Math.min(el0 + halfEl * FEATURE_BADGE.reach, FRAMING.topElevation);
                for (let up = el + FEATURE_BADGE.step; up <= top + 1e-9; up += FEATURE_BADGE.step) {
                    if (!covers(az, up))
                        return [az, up];
                }
                // Away from the lectern; a centred one: the side the feature is on, right when ahead.
                const away = placement.side !== 0 ? -placement.side : az0 < 0 ? -1 : 1;
                const far = halfAz * FEATURE_BADGE.reach;
                for (let off = FEATURE_BADGE.step;; off += FEATURE_BADGE.step) {
                    const side = az + away * off;
                    if (Math.abs(side - az0) > far + 1e-9)
                        return undefined;
                    if (Math.abs(side) > FRAMING.wideHalf + 1e-9)
                        return undefined;
                    if (!covers(side, el))
                        return [side, el];
                }
            })();
            if (found)
                [az, el] = found;
        }
    }
    const direction = fromRig(az, el, yaw);
    const along = crossings(round, eye, direction);
    if (along === undefined || along.far < 0 || (!inRound && along.near < 0)) {
        // Off the (az, el) ray: whether the lectern hides it is judged where it really sits.
        const at = towardEyes(round, eye);
        return {
            at,
            distance: length(sub(at, eye)),
            covered: lecternCovers({ eye, yaw, eyeHeight, placement, assembly }, at),
        };
    }
    // Outside: where it enters. Inside: no further than 4 m, and never short of the round.
    const t = inRound
        ? Math.max(along.inside ? 0 : along.near, Math.min(FEATURE_BADGE.insideDistance, along.far))
        : along.near;
    const at = [
        eye[0] + direction[0] * t,
        eye[1] + direction[1] * t,
        eye[2] + direction[2] * t,
    ];
    return { at, distance: length(sub(at, eye)), covered: covers(az, el) };
}
//# sourceMappingURL=feature-badge.js.map